import { useState } from "react";
import { taskService } from "@miplatform/api";
import { CONFIG_PAGES, HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState, useRouter } from "@miplatform/hooks";
import {
  ISuppressedWorkTemplateBase,
  PlatformModules,
} from "@miplatform/types";
import { useWorkOrderTemplate } from "@mibuildweb/hooks";
import { MODULES_CONFIG_PATHS } from "../../../../routes/config/constants";
import { IWorkOrderTemplateData } from "../ConfigWorkOrderTemplates.types";

export const useWorkOrderTemplateDetailsViewModel = ({
  module,
  workOrderTemplateId,
}: Readonly<{ module: PlatformModules; workOrderTemplateId?: string }>) => {
  const { navigate } = useRouter();
  const { createWorkOrderTemplate } = useWorkOrderTemplate({ module });
  const stateManager = useComponentState();

  const [excludedAccounts, setExcludedAccounts] = useState<
    ISuppressedWorkTemplateBase[]
  >([]);

  const handleCreateWorkOrderTemplate = async (
    data: IWorkOrderTemplateData
  ) => {
    return stateManager.run(async () => {
      const response = await createWorkOrderTemplate(data);
      if (response.status === HTTP_STATUSES.OK) {
        navigate(
          `${MODULES_CONFIG_PATHS[module][CONFIG_PAGES.WORK_ORDER_TEMPLATES]}`
        );
      }
    });
  };

  const fetchExcludedAccounts = async () => {
    if (!workOrderTemplateId) return Promise.resolve(null);

    return stateManager.run(async () => {
      const response = await taskService.suppressedWork.getAll(undefined, {
        filters: {
          workTemplateBaseId: {
            eq: { type: "guid", value: workOrderTemplateId },
          },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        setExcludedAccounts(response.data);
      }
    });
  };

  const excludeWorkOrderTemplate = (accountId: string) => {
    if (!workOrderTemplateId) return Promise.resolve(null);

    return stateManager.run(async () => {
      const response = await taskService.suppressedWork.create(
        {
          accountId,
          workTemplateBaseId: workOrderTemplateId,
        },
        {
          failure: {
            text: "Failed to exclude Work Order Template for selected customer",
          },
        }
      );

      if (response.status === HTTP_STATUSES.OK) {
        await fetchExcludedAccounts();
      }
    });
  };

  const reenableWorkOrderTemplate = (
    suppressedWorkTemplate?: ISuppressedWorkTemplateBase
  ) => {
    if (!suppressedWorkTemplate) return Promise.resolve(null);

    const { id, modifiedOn } = suppressedWorkTemplate;

    return stateManager.run(async () => {
      const response = await taskService.suppressedWork.delete(id, modifiedOn, {
        failure: {
          text: "Failed to reenable Work Order Template for selected customer",
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        await fetchExcludedAccounts();
      }
    });
  };

  return {
    isLoading: stateManager.isBusy(),
    handleCreateWorkOrderTemplate,
    excludedAccounts,
    fetchExcludedAccounts,
    excludeWorkOrderTemplate,
    reenableWorkOrderTemplate,
  };
};
